import React, { useState, useEffect } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  onSnapshot,
  setDoc,
  serverTimestamp,
  runTransaction,
  orderBy,
  limit
} from 'firebase/firestore';
import {
  ArrowLeft,
  Truck,
  RefreshCw,
  Clock,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Send,
  X,
  Loader,
  User,
  Download
} from 'lucide-react';
import { LoadoutAllocation } from '../types/inventory';
import { Warehouse, Vehicle, WarehouseDailyPricing } from '../types';
import { SystemInitializationService } from '../services/systemInitialization';
import { pushUnsyncedSalesThenPull, buildAggregatedTallyHeaders } from '../services/tallyService';
import { db, getDocuments } from '../services/firebase';
import { useAuth } from '../contexts/AuthContext';

interface LoadoutPageProps {
  onBack: () => void;
}

interface ProductStock {
  productId: string;
  productName: string;
  unit: string;
  availableStock: number;
  category: string;
  reorderLevel?: number;
}

interface VehicleLoad {
  vehicleId: string;
  vehicleNumber: string;
  vehicleType: string;
  capacity: number;
  currentLoad: number;
  allocations: LoadoutAllocation[];
  assignedSalesman?: string;
}

const LoadoutPage: React.FC<LoadoutPageProps> = ({ onBack }) => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [syncLoading, setSyncLoading] = useState(false);
  const [showLiveSyncConfirm, setShowLiveSyncConfirm] = useState(false);
  
  // Environment detection
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  
  // Selection states
  const [selectedWarehouse, setSelectedWarehouse] = useState<string>('');
  const [selectedDate] = useState<string>(new Date().toISOString().split('T')[0]);
  
  // Data states
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  // Route-centric data
  const [routes, setRoutes] = useState<any[]>([]);
  // Store custom routeId (e.g., RT002) in activeRouteTab and all route-based maps
  const [activeRouteTab, setActiveRouteTab] = useState<string>('');
  const [routeStates, setRouteStates] = useState<{[routeId: string]: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled'}>({});
  const [routeInventory, setRouteInventory] = useState<{[routeId: string]: {[productId: string]: number}}>({});
  // Issued allocations per route (from detailedAllocations during 'issuing')
  const [routeIssuedAllocations, setRouteIssuedAllocations] = useState<{[routeId: string]: {[productId: string]: number}}>({});
  
  // Settlement validation
  const [loadoutBlocked, setLoadoutBlocked] = useState(false);
  const [blockingMessage, setBlockingMessage] = useState<string>('');
  // Per-route assignment info (from beats for selected date)
  const [routeAssignments, setRouteAssignments] = useState<{[routeId: string]: { vehicleId?: string; vehicleName?: string; salesmanId?: string; salesmanName?: string }}>({});
  // Legacy vehicle array retained for compatibility where referenced indirectly
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [productStocks, setProductStocks] = useState<ProductStock[]>([]);
  const [vehicleLoads, setVehicleLoads] = useState<VehicleLoad[]>([]);
  
  // All products for pricing operations (separate from inventory-based productStocks)
  const [allProducts, setAllProducts] = useState<any[]>([]);
  
  // UI states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState<ProductStock | null>(null);
  const [allocationQuantity, setAllocationQuantity] = useState<number>(0);

  // State for active vehicle tab and load management
  const [activeVehicleTab, setActiveVehicleTab] = useState<string>('');
  // Generic per-tab load map (now keyed by routeId)
  const [vehicleLoadData, setVehicleLoadData] = useState<{[key: string]: {[productId: string]: number}}>({});
  
  // State management for vehicles (planning, issuing, on_field, settling, settled)
  const [vehicleStates, setVehicleStates] = useState<{[vehicleId: string]: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled'}>({});
  const [vehicleSnapshots, setVehicleSnapshots] = useState<{[vehicleId: string]: {[productId: string]: number}}>({});
  
  // Vehicle inventory data
  const [vehicleInventory, setVehicleInventory] = useState<{[vehicleId: string]: {[productId: string]: number}}>({});
  
  // Previous day's pricing data
  const [prevDayPricing, setPrevDayPricing] = useState<{[productId: string]: number}>({});
  
  // Current day's pricing data (editable)
  const [currentDayPricing, setCurrentDayPricing] = useState<{[productId: string]: number}>({});
  
  // Modal state for pricing initialization
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [lastAvailablePricing, setLastAvailablePricing] = useState<{[productId: string]: {rate: number, date: string}}>({});
  const [modalPricing, setModalPricing] = useState<{[productId: string]: number}>({});
  
  // NOTE: Vehicle-centric beat loadout state removed (route-first now authoritative)
  
  // 🔥 Additional data on Salesman and Route 🔥
  // Removed beat/user-based route assignments; we build purely from routes collection

  
  // System Initialization states
  const [showInitializeModal, setShowInitializeModal] = useState(false);
  // CSV upload removed – initialization is cleanup-only now
  const [initializationProgress, setInitializationProgress] = useState<{
    stage: string;
    message: string;
    progress: number;
  } | null>(null);
  const [initializationResult, setInitializationResult] = useState<string>('');
  const [confirmText, setConfirmText] = useState<string>('');
  
  // Zoho integration removed (Tally-only sync)

  useEffect(() => {
    loadInitialData();
    // Removed Zoho OAuth callback handling
  }, []);
  // Removed Zoho post-auth action checker

  // Dedicated useEffect for warehouse data - only runs when selectedWarehouse is actually set
  useEffect(() => {
    if (selectedWarehouse) {
      loadWarehouseData();
    }
  }, [selectedWarehouse]);

  // Check for unsettled routes when warehouse changes
  useEffect(() => {
    if (selectedWarehouse && warehouses.length > 0) {
      const warehouse = warehouses.find(w => w.id === selectedWarehouse);
      if (warehouse) {
        const warehouseCustomId = warehouse.warehouseId || selectedWarehouse;
        checkForUnsettledRoutes(warehouseCustomId);
      }
    }
  }, [selectedWarehouse, warehouses]);

  // Route status listener when routes/warehouse/date are ready
  useEffect(() => {
    if (!selectedWarehouse || routes.length === 0) return;
    const unsubscribePromise = loadRouteStatuses();
    return () => {
      // detach when unmounting or deps change
      if (typeof unsubscribePromise === 'function') {
        try { (unsubscribePromise as any)(); } catch {}
      }
    };
  }, [routes, selectedWarehouse, selectedDate]);

  // Check for earliest unsettled date that blocks new loadouts
  const checkForUnsettledRoutes = async (warehouseCustomId: string) => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Query for non-settled statuses to avoid != inequality filter
      const unsettledQuery = query(
        collection(db, 'route_daily_status'),
        where('warehouseId', '==', warehouseCustomId),
        where('status', 'in', ['planning', 'issuing', 'on_field', 'settling'])
      );
      
      const unsettledSnapshot = await getDocs(unsettledQuery);
      const unsettledRoutes = unsettledSnapshot.docs.map(doc => doc.data());
      
      // Filter for dates before today and find earliest
      const pastUnsettledRoutes = unsettledRoutes.filter(route => route.date < today);
      
      if (pastUnsettledRoutes.length === 0) {
        setLoadoutBlocked(false);
        setBlockingMessage('');
        return;
      }
      
      // Find earliest unsettled date
      const earliestDate = pastUnsettledRoutes.map(route => route.date).sort()[0];
      const routesForEarliestDate = pastUnsettledRoutes.filter(route => route.date === earliestDate);
      
      setLoadoutBlocked(true);
      setBlockingMessage(`Settlement required for ${earliestDate} (${routesForEarliestDate.length} route${routesForEarliestDate.length > 1 ? 's' : ''} pending)`);
      
      console.log('🚫 Loadout blocked - earliest unsettled date:', earliestDate);
    } catch (error) {
      console.error('Error checking for unsettled routes:', error);
      setLoadoutBlocked(false);
      setBlockingMessage('');
    }
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load warehouses using the same pattern as AddOutlet.tsx
      const [warehousesData, routesData, beatsData] = await Promise.all([
        getDocuments('warehouses'),
        getDocuments('routes'), 
        getDocuments('beats')
      ]);
      // Store routes for later tabs
      setRoutes(routesData as any[]);
      
      // Get current user's active route from beats table (same as AddOutlet pattern)
      const today = new Date().toISOString().split('T')[0].replace(/-/g, '-');
      const userActiveBeat = beatsData.find((beat: any) => 
        beat.salesmanId === currentUser?.id && 
        beat.isActive && 
        beat.forDate === today
      );
      
      // Filter warehouses based on user's route assignment (following AddOutlet pattern)
      let availableWarehouses = warehousesData.filter((warehouse: any) => warehouse.isActive !== false);
      
      if (userActiveBeat && userActiveBeat.routeId) {
        // Find the user's route by custom routeId
        const userRoute = routesData.find((route: any) => route.routeId === userActiveBeat.routeId);
        if (userRoute && userRoute.warehouseId) {
          // Show user's assigned warehouse plus any other active warehouses
          const userWarehouse = warehousesData.find((w: any) => w.warehouseId === userRoute.warehouseId);
          if (userWarehouse) {
            // Prioritize user's assigned warehouse but include others
            availableWarehouses = [
              userWarehouse,
              ...warehousesData.filter((w: any) => 
                w.warehouseId !== userRoute.warehouseId && w.isActive !== false
              )
            ];
          }
        }
      }
      
      setWarehouses(availableWarehouses);
      
      // Auto-select user's assigned warehouse if available, otherwise first warehouse
      if (availableWarehouses.length > 0 && !selectedWarehouse) {
        const userWarehouseCustomId = userActiveBeat?.routeId ? 
          routesData.find((route: any) => route.routeId === userActiveBeat.routeId)?.warehouseId : null;

        const userWarehouseDoc = userWarehouseCustomId
          ? availableWarehouses.find((w: any) => w.warehouseId === userWarehouseCustomId)
          : null;

        const warehouseDocIdToSelect = userWarehouseDoc?.id || availableWarehouses[0].id;
        setSelectedWarehouse(warehouseDocIdToSelect);
      }
      
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllProductsForPricing = async () => {
    try {
      // Load ALL active products for pricing operations
      const productsSnapshot = await getDocs(
        query(collection(db, 'products'), where('isActive', '==', true))
      );
      
      const productsData = productsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          productId: data.productId || doc.id,
          productName: data.name || data.productName || 'Unknown Product',
          unit: data.unit || 'KG',
          category: data.category || data.categoryName || 'Unknown',
          ...data
        };
      });
      
      setAllProducts(productsData);
      return productsData;
      
    } catch (error) {
      console.error('Error loading all products for pricing:', error);
      return [];
    }
  };

  const loadWarehouseData = async () => {
    if (!selectedWarehouse) return;
    
    try {
      setLoading(true);
      
      // Get the selected warehouse document to get its warehouseId field
      const selectedWarehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
      if (!selectedWarehouseDoc || !selectedWarehouseDoc.warehouseId) {
        console.error('Selected warehouse not found or missing warehouseId:', selectedWarehouse);
        setVehicles([]);
        setLoading(false);
        return;
      }
      
      // Load ROUTES for the selected warehouse (match by warehouse custom ID)
      const routesSnapshot = await getDocs(
        query(
          collection(db, 'routes'),
          where('warehouseId', '==', selectedWarehouseDoc.warehouseId),
        )
      );
      const routesData = routesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as any));

      // Store the routes for this warehouse
      setRoutes(routesData);
      // Auto-select the first route (by custom routeId) if none selected yet
      if (!activeRouteTab) {
        const firstWithCustomId = routesData.find((r: any) => !!r.routeId);
        if (firstWithCustomId?.routeId) {
          setActiveRouteTab(firstWithCustomId.routeId);
        }
      }

      // No beats/users join; tabs and planning derive solely from routes

      // Read Salesman/Vehicle assignments for routes from beats (for selected date)
      try {
        // Fetch beats for selected date using both forDate (new) and date (legacy) fields
        const [beatsForDateSnap, beatsLegacyDateSnap] = await Promise.all([
          getDocs(query(collection(db, 'beats'), where('forDate', '==', selectedDate))),
          getDocs(query(collection(db, 'beats'), where('date', '==', selectedDate)))
        ]);

        // Merge unique docs by id
        const beatDocsMap = new Map<string, any>();
        [...beatsForDateSnap.docs, ...beatsLegacyDateSnap.docs].forEach(d => {
          beatDocsMap.set(d.id, d.data());
        });

        const assignments: {[routeId: string]: { vehicleId?: string; vehicleName?: string; salesmanId?: string; salesmanName?: string }} = {};
        beatDocsMap.forEach((beat: any) => {
          // Enforce custom routeId only; ignore legacy routeDocumentId
          const rid: string | undefined = beat.routeId;
          if (!rid) return;
          const routeDoc: any | undefined = routesData.find((r: any) => r.routeId === rid);
          if (!routeDoc) return;
          // Ensure assignment belongs to currently selected warehouse (by warehouse custom id)
          if (routeDoc.warehouseId !== selectedWarehouseDoc.warehouseId) return;
          // If multiple beats per route exist, prefer active assignments
          const existing = assignments[rid];
          const preferNew = beat.isActive === true; // prefer active beats
          if (!existing || preferNew) {
            assignments[rid] = {
              vehicleId: beat.vehicleId,
              vehicleName: beat.vehicleName,
              salesmanId: beat.salesmanId,
              salesmanName: beat.salesmanName,
            } as any;
          }
        });
        setRouteAssignments(assignments);
      } catch (err) {
        console.warn('Failed loading route assignments from beats:', err);
        setRouteAssignments({});
      }

      // Load ROUTE inventory data using custom routeId as warehouseId
      const routeInventoryData: { [routeId: string]: { [productId: string]: number } } = {};
      for (const route of routesData) {
        const rid = route.routeId; // enforce custom id only
        if (!rid) continue;
        routeInventoryData[rid] = {};
        const routeInvSnapshot = await getDocs(
          query(
            collection(db, 'inventory'),
            // Treat route as sub-warehouse keyed by custom routeId
            where('warehouseId', '==', rid)
          )
        );
        routeInvSnapshot.docs.forEach(docSnap => {
          const inv = docSnap.data();
          routeInventoryData[rid][inv.productId] = inv.closingStock || 0;
        });
      }
      setRouteInventory(routeInventoryData);

      // Initialize per-route load map without wiping existing planned data.
      // Seed only missing route keys to avoid losing planned/issuing quantities during reloads.
      setVehicleLoadData(prev => {
        const next = { ...prev } as { [key: string]: { [productId: string]: number } };
        routesData.forEach((r: any) => {
          if (r.routeId && next[r.routeId] === undefined) {
            next[r.routeId] = {};
          }
        });
        return next;
      });
      
      // 🔧 FIX: Load live inventory using warehouse's warehouseId field instead of document ID
      const inventorySnapshot = await getDocs(
        query(
          collection(db, 'inventory'),
          where('warehouseId', '==', selectedWarehouseDoc.warehouseId)
        )
      );
      
      const inventoryData = inventorySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as any;
      });
      
      // Load products collection to get product names
      const productsSnapshot = await getDocs(collection(db, 'products'));
      
      const productsData = productsSnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data
        } as any; // Cast to any to access properties dynamically
      });
      
      // Transform to ProductStock format, joining with products data
      const stocks = inventoryData.map(inv => {
        // Try multiple lookup strategies for better product matching
        let product = productsData.find((p: any) => p.id === inv.productId);
        
        if (!product) {
          // Try matching by productId field if document ID doesn't work
          product = productsData.find((p: any) => p.productId === inv.productId);
        }
        
        const productName = product?.name || product?.productName || `Unknown Product (${inv.productId})`;
        const unit = product?.unit || inv.unit || 'KG';
        const category = product?.category || product?.categoryName || inv.category || 'Unknown';
        
        return {
          productId: inv.productId,
          productName,
          unit,
          availableStock: inv.closingStock || 0,
          category,
          reorderLevel: product?.reorderLevel || 10
        };
      });
      
      setProductStocks(stocks);
      
      // Load all products first for pricing operations
      await loadAllProductsForPricing();
      
      // Load previous day's pricing using ALL products, not just inventory-based ones
      await loadLastAvailablePricing(selectedWarehouseDoc.warehouseId, true);
      
  // Inventory and statuses are now route-based via loadRouteStatuses()
      
    } catch (error) {
      console.error('Error loading warehouse data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadLastAvailablePricing = async (warehouseCustomId: string, useAllProducts?: boolean) => {
    try {
      const todayStr = selectedDate;
      
      // Use all products for pricing if specified, otherwise use inventory-based products
      let productsToProcess;
      if (useAllProducts) {
        // Load all products for pricing operations
        productsToProcess = allProducts.length > 0 ? allProducts : await loadAllProductsForPricing();
      } else {
        // Use existing inventory-based products for backward compatibility
        productsToProcess = productStocks;
      }
      
      console.log('🔍 PRICING DEBUG:', {
        warehouseCustomId,
        todayStr,
        useAllProducts,
        productCount: productsToProcess.length,
        productStocks: productsToProcess.map(p => ({ id: p.productId, name: p.productName }))
      });
      
      // STEP A: Check if today's pricing exists
      const currentPricingSnapshot = await getDocs(
        query(
          collection(db, 'warehouse_daily_pricing'),
          where('warehouseId', '==', warehouseCustomId),
          where('date', '==', todayStr),
          where('isActive', '==', true)
        )
      );
      
      console.log('🔍 CURRENT PRICING QUERY RESULTS:', {
        empty: currentPricingSnapshot.empty,
        size: currentPricingSnapshot.size,
        docs: currentPricingSnapshot.docs.map(d => ({ id: d.id, data: d.data() }))
      });
      
      // STEP B: If today's pricing exists, load it and move on (no modal)
      if (!currentPricingSnapshot.empty) {
        console.log('✅ Today\'s pricing exists - loading it');
        const currentPricingData: {[productId: string]: number} = {};
        currentPricingSnapshot.docs.forEach(doc => {
          const pricing = doc.data();
          currentPricingData[pricing.productId] = pricing.unitRate || 0;
          console.log('📝 Loading pricing:', { productId: pricing.productId, rate: pricing.unitRate });
        });
        setCurrentDayPricing(currentPricingData);
        setShowPricingModal(false);
        console.log('🎯 Pricing modal set to FALSE - today\'s pricing loaded');
        return; // Exit early - no modal needed
      }
      
      // STEP C: No today's pricing - show modal and load historical data
      console.log('❌ No today\'s pricing found - showing modal');
      await loadHistoricalPricingForModal(warehouseCustomId, productsToProcess);
      
    } catch (error) {
      console.error('Error loading last available pricing:', error);
      setShowPricingModal(true); // Show modal anyway to allow manual pricing
    }
  };
  
  // Helper function to load historical pricing data for the modal
  const loadHistoricalPricingForModal = async (warehouseCustomId: string, productsToProcess: any[]) => {
    try {
      const lastPricingData: {[productId: string]: {rate: number, date: string}} = {};
      
      // For each product, get the most recent pricing within 30 days
      for (const product of productsToProcess) {
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        const thirtyDaysAgoStr = thirtyDaysAgo.toISOString().split('T')[0];
        
        const productPricingSnapshot = await getDocs(
          query(
            collection(db, 'warehouse_daily_pricing'),
            where('warehouseId', '==', warehouseCustomId),
            where('productId', '==', product.productId),
            where('isActive', '==', true),
            where('date', '>=', thirtyDaysAgoStr)
          )
        );
        
        if (!productPricingSnapshot.empty) {
          // Sort by date in memory to get the most recent
          const sortedDocs = productPricingSnapshot.docs.sort((a, b) => {
            const dateA = a.data().date || '';
            const dateB = b.data().date || '';
            return dateB.localeCompare(dateA); // Descending order
          });
          const lastPricing = sortedDocs[0].data();
          lastPricingData[product.productId] = {
            rate: lastPricing.unitRate || 0,
            date: lastPricing.date
          };
        } else {
          lastPricingData[product.productId] = {
            rate: 0,
            date: 'No history'
          };
        }
      }
      
      setLastAvailablePricing(lastPricingData);
      
      // Initialize modal pricing with last available rates
      const modalInitialPricing: {[productId: string]: number} = {};
      Object.keys(lastPricingData).forEach(productId => {
        modalInitialPricing[productId] = lastPricingData[productId].rate;
      });
      setModalPricing(modalInitialPricing);
      
      // Show pricing modal
      setShowPricingModal(true);
      
    } catch (error) {
      console.error('Error loading historical pricing data:', error);
      setShowPricingModal(true); // Show modal anyway to allow manual pricing
    }
  };

  const handlePriceChange = async (productId: string, productName: string, newPrice: number) => {
    try {
      const warehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
      if (!warehouseDoc) return;
      
      // Update local state immediately
      setCurrentDayPricing(prev => ({
        ...prev,
        [productId]: newPrice
      }));
      
      // Update database
      const pricingDocId = `${warehouseDoc.warehouseId}_${selectedDate}_${productId}`;
      await setDoc(doc(db, 'warehouse_daily_pricing', pricingDocId), {
        id: pricingDocId,
        warehouseId: warehouseDoc.warehouseId,
        date: selectedDate,
        productId: productId,
        productName: productName,
        unitRate: newPrice,
        isActive: true,
        updatedAt: serverTimestamp(),
        updatedBy: currentUser?.id || 'warehouse-user',
        createdAt: serverTimestamp(),
        createdBy: currentUser?.id || 'warehouse-user'
      }, { merge: true });
      
    } catch (error) {
      console.error('Error updating price:', error);
      // Optionally show error to user
    }
  };

  const handleSetTodaysPricing = async () => {
    try {
      const warehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
      if (!warehouseDoc) return;
      
      const timestamp = serverTimestamp();
      
      // Batch create pricing entries
      await runTransaction(db, async (transaction) => {
        Object.keys(modalPricing).forEach(productId => {
          const product = allProducts.find(p => p.productId === productId);
          if (!product) return;
          
          const pricingDocId = `${warehouseDoc.warehouseId}_${selectedDate}_${productId}`;
          const pricingRef = doc(db, 'warehouse_daily_pricing', pricingDocId);
          
          transaction.set(pricingRef, {
            id: pricingDocId,
            warehouseId: warehouseDoc.warehouseId,
            date: selectedDate,
            productId: productId,
            productName: product.productName,
            unitRate: modalPricing[productId],
            isActive: true,
            updatedAt: timestamp,
            updatedBy: currentUser?.id || 'system',
            createdAt: timestamp,
            createdBy: currentUser?.id || 'system'
          });
        });
      });
      
      // Update current day pricing state
      setCurrentDayPricing({...modalPricing});
      
      // Close modal
      setShowPricingModal(false);
      
      alert('Today\'s pricing has been set successfully!');
      
    } catch (error) {
      console.error('Error setting today\'s pricing:', error);
      alert('Error setting pricing. Please try again.');
    }
  };

  const loadRouteStatuses = async () => {
    if (!selectedWarehouse || routes.length === 0) return;
    try {
      const today = selectedDate;
      const wh = warehouses.find(w => w.id === selectedWarehouse);
      const warehouseCustomId = wh?.warehouseId || selectedWarehouse;
      const statusQueryRef = query(
        collection(db, 'route_daily_status'),
        // Match by warehouse custom ID
        where('warehouseId', '==', warehouseCustomId),
        where('date', '==', today)
      );

      const unsubscribe = onSnapshot(statusQueryRef, async (snapshot) => {
        const previousStates = { ...routeStates };
        const foundStatuses: { [routeId: string]: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled' } = {};
        const foundLoadData: { [routeId: string]: { [productId: string]: number } } = {};
        const foundIssuedData: { [routeId: string]: { [productId: string]: number } } = {};

        snapshot.docs.forEach(docSnap => {
          const data = docSnap.data() as any; // RouteDailyStatus
          // Enforce custom routeId (e.g., RT002)
          const rid = data.routeId;
          if (!rid) return;
          foundStatuses[rid] = data.status;
          // Prefer plannedLoads; if absent/empty, derive from detailedAllocations (issuedQty fallback)
          let loads = data.plannedLoads || {};
          if ((!loads || Object.keys(loads).length === 0) && Array.isArray(data.detailedAllocations)) {
            const derived: { [productId: string]: number } = {};
            data.detailedAllocations.forEach((a: any) => {
              const qty = (a?.issuedQty ?? a?.allocatedQty ?? 0) as number;
              if (a?.productId && qty > 0) derived[a.productId] = qty;
            });
            loads = derived;
          }
          foundLoadData[rid] = loads;

          // Always capture issued allocations map from detailedAllocations for 'issuing' display
          if (Array.isArray(data.detailedAllocations)) {
            const issuedMap: { [productId: string]: number } = {};
            data.detailedAllocations.forEach((a: any) => {
              const qty = (a?.issuedQty ?? a?.allocatedQty ?? 0) as number;
              if (a?.productId && qty > 0) issuedMap[a.productId] = qty;
            });
            foundIssuedData[rid] = issuedMap;
          }
        });

        // Default missing routes to 'planning'
        const allStatuses: { [routeId: string]: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled' } = {};
        const allLoadData: { [routeId: string]: { [productId: string]: number } } = {};
        for (const r of routes) {
          const rid = (r as any).routeId as string | undefined;
          if (!rid) continue;
          allStatuses[rid] = foundStatuses[rid] || 'planning';
          allLoadData[rid] = foundLoadData[rid] || {};
          if (!foundIssuedData[rid]) foundIssuedData[rid] = {};
        }

        const hasNewOnField = Object.entries(allStatuses).some(([rid, newStatus]) => {
          const prevStatus = previousStates[rid];
          return newStatus === 'on_field' && prevStatus !== 'on_field';
        });

        const hasNewSettled = Object.entries(allStatuses).some(([rid, newStatus]) => {
          const prevStatus = previousStates[rid];
          return newStatus === 'settled' && prevStatus !== 'settled';
        });

        setRouteStates(allStatuses);
        setVehicleLoadData(allLoadData); // reuse generic map now keyed by routeId
  setRouteIssuedAllocations(foundIssuedData);

        if (hasNewOnField || hasNewSettled) {
          setTimeout(() => {
            loadWarehouseData();
          }, 1000);
        }
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error loading route statuses:', error);
    }
  };

  // Load individual vehicle inventory for vehicles that just went on_field
  const loadIndividualVehicleInventory = async (
    allStatuses: {[vehicleId: string]: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled'}, 
    previousStates: {[vehicleId: string]: 'planning' | 'issuing' | 'on_field' | 'settling' | 'settled'}
  ) => {
    try {
      const vehicleInventoryUpdate: {[vehicleId: string]: {[productId: string]: number}} = { ...vehicleInventory };
      
      // Find vehicles that just transitioned to 'on_field'
      const onFieldVehicles = Object.entries(allStatuses).filter(([vehicleId, newStatus]) => {
        const prevStatus = previousStates[vehicleId];
        return newStatus === 'on_field' && prevStatus !== 'on_field';
      });
      
      console.log('🔍 Loading inventory for vehicles that just went on_field:', onFieldVehicles);
      
      for (const [vehicleId, status] of onFieldVehicles) {
        const vehicle = vehicles.find(v => v.vehicleId === vehicleId);
        if (!vehicle) {
          console.warn('Vehicle not found for ID:', vehicleId);
          continue;
        }
        
        console.log(`🔍 Loading inventory for vehicle ${vehicle.vehicleNumber} (${vehicleId})`);
        
        // Query inventory using vehicle custom ID (vehicleId field, like "VEH-002")
        const vehicleInvSnapshot = await getDocs(
          query(
            collection(db, 'inventory'),
            where('warehouseId', '==', vehicle.vehicleId) // Use custom vehicle ID
          )
        );
        
        console.log(`🔍 Found ${vehicleInvSnapshot.docs.length} inventory records for ${vehicle.vehicleNumber}`);
        
        const vehicleProducts: {[productId: string]: number} = {};
        vehicleInvSnapshot.docs.forEach(doc => {
          const invData = doc.data();
          console.log('🔍 Vehicle inventory record:', {
            productId: invData.productId,
            productName: invData.productName,
            closingStock: invData.closingStock,
            warehouseId: invData.warehouseId
          });
          vehicleProducts[invData.productId] = invData.closingStock || 0;
        });
        
        vehicleInventoryUpdate[vehicleId] = vehicleProducts;
        console.log(`🔍 Updated inventory for ${vehicle.vehicleNumber}:`, vehicleProducts);
      }
      
      // Update the state with new inventory data
      setVehicleInventory(vehicleInventoryUpdate);
      console.log('🔍 Final vehicleInventory state updated for on_field vehicles');
      
    } catch (error) {
      console.error('Error loading individual vehicle inventory:', error);
    }
  };

  // NOTE: Legacy beat_loadout/vehicle_daily_status pending reads removed (route_daily_status is the source of truth)

  // Smart sync button that handles Tally stock update
  const handleSyncInventory = async (useTestMode = false) => {
    try {
      setSyncLoading(true);
      const targetLabel = useTestMode ? 'TEST Firebase' : 'LIVE Firebase';
      
      // Push unsynced sales/replace to Tally, then pull fresh stock
      const result = await pushUnsyncedSalesThenPull(
        warehouses.find(w => w.id === selectedWarehouse)?.warehouseId || undefined,
        20,
        useTestMode
      );
      
      console.log('Tally push+pull completed:', result);
      
      // Consolidated summary for modal/alert
      const pushLines = Array.isArray((result as any).pushDebug) ? (result as any).pushDebug : [];
      const linesJoined = pushLines.length > 0 ? `\n\nDetails:\n- ${pushLines.join('\n- ')}` : '';
      // If the first result has deep XML debug, show it (truncated) for copy/paste
      const firstDebug: any = (result as any).rawResults?.[0]?.debug || (result as any).results?.[0]?.debug || {};
      const reqXml = firstDebug.requestXML ? `\n\n-- Request XML (truncated) --\n${String(firstDebug.requestXML).slice(0,4000)}` : '';
      const respXml = firstDebug.responseXML ? `\n\n-- Response XML (truncated) --\n${String(firstDebug.responseXML).slice(0,4000)}` : '';
      const pushSummary = `Pushed: ${result.pushed}\nSucceeded: ${result.succeeded}\nFailed: ${result.failed}${linesJoined}${reqXml}${respXml}`;
      const pullSummary = result.pulled && result.pullSummary
        ? `Pulled OK: Yes\nRecords Found: ${result.pullSummary.summary?.tally_records_found}\nDeleted: ${result.pullSummary.summary?.deleted_count}\nInserted: ${result.pullSummary.summary?.inserted_count}`
        : `Pulled OK: ${result.pulled ? 'Yes' : 'No'}${result.failed > 0 ? ' (blocked due to push failures)' : ''}`;
      
      alert(`✅ ERP Sync Completed (${targetLabel})\n\n-- Push to Tally --\n${pushSummary}\n\n-- Pull from Tally --\n${pullSummary}`);
      
      // Reload warehouse data to show updated inventory
      await loadWarehouseData();
      
    } catch (error: any) {
      console.error('Tally sync failed:', error);
      alert(`❌ Tally Sync Failed\n\n${error?.message || 'Unknown error occurred'}`);
    } finally {
      setSyncLoading(false);
    }
  };

  // Download-only: build aggregated headers from settlements and generate XML locally (no gateway call)
  const handleDownloadTallyXML = async () => {
    try {
      setSyncLoading(true);
      const warehouseCustomId = warehouses.find(w => w.id === selectedWarehouse)?.warehouseId;
      if (!warehouseCustomId) {
        alert('Please select a warehouse first.');
        return;
      }
      // 1) Build aggregated headers (same as real push flow) from settlements
      const today = new Date();
      const pad = (n: number) => String(n).padStart(2, '0');
      const ymd = `${today.getFullYear()}-${pad(today.getMonth()+1)}-${pad(today.getDate())}`;
      const { headersToPush } = await buildAggregatedTallyHeaders(warehouseCustomId, ymd);

      if (!headersToPush || headersToPush.length === 0) {
        alert('No aggregated sales/replacements found to export.');
        return;
      }

      // 2) Generate Tally XML locally (mirrors backend builder)
      const xmlEscape = (s: string) => (s || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');

      const toTallyDate = (ymdStr: string) => {
        const d = new Date(ymdStr);
        const year = d.getFullYear();
        const mm = String(d.getMonth()+1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${year}${mm}${dd}`;
      };

      const buildVoucher = (vchtype: string, header: any, lines: any[], idKey: string, voucherNo: string) => {
        const dateYmd = toTallyDate(header.date);
        const partyLedger = 'Cash';
        const salesLedger = 'Cash_Sales';
        const saleVch = vchtype.toLowerCase() === 'sales';
        const total = lines.reduce((s, ln) => s + Number(ln.quantity || 0) * Number(ln.rate || 0), 0);
        const partyAmount = saleVch ? total : -total;
        const salesAmount = saleVch ? -total : total;
        const invXml = lines.map(ln => {
          const qty = Number(ln.quantity || 0);
          const rate = Number(ln.rate || 0);
          const unit = (ln.unit || 'Pcs').trim();
          const amount = saleVch ? -(qty * rate) : (qty * rate);
          const billedQty = `${qty} ${unit}`;
          const rateTxt = `${rate.toFixed(2)}/ ${unit}`;
          const stockItemName = xmlEscape(String(ln.productName || ln.productId));
          const godownXml = header.warehouseId
            ? `\n  <BATCHALLOCATIONS.LIST>\n    <GODOWNNAME>${xmlEscape(header.warehouseId)}</GODOWNNAME>\n    <BILLEDQTY>${billedQty}</BILLEDQTY>\n    <ACTUALQTY>${billedQty}</ACTUALQTY>\n    <AMOUNT>${amount.toFixed(2)}</AMOUNT>\n  </BATCHALLOCATIONS.LIST>`
            : '';
          return `\n<ALLINVENTORYENTRIES.LIST>\n  <STOCKITEMNAME>${stockItemName}</STOCKITEMNAME>\n  <ISDEEMEDPOSITIVE>${saleVch ? 'Yes' : 'No'}</ISDEEMEDPOSITIVE>\n  <RATE>${rateTxt}</RATE>\n  <AMOUNT>${amount.toFixed(2)}</AMOUNT>\n  <BILLEDQTY>${billedQty}</BILLEDQTY>\n  <ACTUALQTY>${billedQty}</ACTUALQTY>${godownXml}\n</ALLINVENTORYENTRIES.LIST>`;
        }).join('');
        return `\n<TALLYMESSAGE xmlns:UDF="TallyUDF">\n  <VOUCHER VCHTYPE="${xmlEscape(vchtype)}" ACTION="Create">\n    <DATE>${dateYmd}</DATE>\n    <VOUCHERNUMBER>${xmlEscape(voucherNo)}</VOUCHERNUMBER>\n    <PARTYLEDGERNAME>${xmlEscape(partyLedger)}</PARTYLEDGERNAME>\n    <BASICBASEPARTYNAME>${xmlEscape(partyLedger)}</BASICBASEPARTYNAME>\n    <ISINVOICE>Yes</ISINVOICE>\n    <REFERENCE>${xmlEscape(idKey)}</REFERENCE>\n    <NARRATION>${xmlEscape(`SFA Txn ${idKey}`)}</NARRATION>\n\n    <LEDGERENTRIES.LIST>\n      <LEDGERNAME>${xmlEscape(partyLedger)}</LEDGERNAME>\n      <ISDEEMEDPOSITIVE>${saleVch ? 'No' : 'Yes'}</ISDEEMEDPOSITIVE>\n      <AMOUNT>${partyAmount.toFixed(2)}</AMOUNT>\n    </LEDGERENTRIES.LIST>\n\n    <LEDGERENTRIES.LIST>\n      <LEDGERNAME>${xmlEscape(salesLedger)}</LEDGERNAME>\n      <ISDEEMEDPOSITIVE>${saleVch ? 'Yes' : 'No'}</ISDEEMEDPOSITIVE>\n      <AMOUNT>${salesAmount.toFixed(2)}</AMOUNT>\n    </LEDGERENTRIES.LIST>\n    ${invXml}\n  </VOUCHER>\n</TALLYMESSAGE>`;
      };

      const vouchers = headersToPush.map(h => {
        const saleLines = (h.lines || []).filter((ln: any) => ln.type === 'sale' && Number(ln.quantity || 0) > 0);
        const replaceLines = (h.lines || []).filter((ln: any) => ln.type === 'replace' && Number(ln.quantity || 0) > 0);
        const msgs: string[] = [];
        if (saleLines.length) msgs.push(buildVoucher('Sales', h, saleLines, h.idempotencyKey, h.invoiceNo));
        if (replaceLines.length) msgs.push(buildVoucher('Sales Return', h, replaceLines, `${h.idempotencyKey}-RET`, `RET-${h.invoiceNo}`));
        return msgs.join('\n');
      }).join('\n');

      const envelope = `<?xml version="1.0" encoding="UTF-8"?>\n<ENVELOPE>\n  <HEADER>\n    <TALLYREQUEST>Import Data</TALLYREQUEST>\n  </HEADER>\n  <BODY>\n    <IMPORTDATA>\n      <REQUESTDESC>\n        <REPORTNAME>Vouchers</REPORTNAME>\n        <STATICVARIABLES>\n          <SVCURRENTCOMPANY>Test</SVCURRENTCOMPANY>\n        </STATICVARIABLES>\n      </REQUESTDESC>\n      <REQUESTDATA>${vouchers}\n      </REQUESTDATA>\n    </IMPORTDATA>\n  </BODY>\n</ENVELOPE>`;

      // 3) Download as file
      const blob = new Blob([envelope], { type: 'application/xml;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tally-vouchers-${warehouseCustomId}-${ymd}.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Download XML failed:', err);
      alert(`Failed to build XML: ${err?.message || err}`);
    } finally {
      setSyncLoading(false);
    }
  };

  // Update planned quantity for the active route and persist to route_daily_status
  const updateLoadQuantity = async (productId: string, newQuantity: number) => {
    if (!activeRouteTab) return;
    // Local state update
    setVehicleLoadData(prev => ({
      ...prev,
      [activeRouteTab]: {
        ...(prev[activeRouteTab] || {}),
        [productId]: newQuantity
      }
    }));

    // Save to Firestore
    try {
      const docId = `${activeRouteTab}_${selectedDate}`;
      const statusRef = doc(db, 'route_daily_status', docId);
      const existingData = vehicleLoadData[activeRouteTab] || {};
      const updatedLoads = { ...existingData, [productId]: newQuantity };
      // NEW: Build carry-over allocations for planning stage so downstream UIs (salesman/settlement)
      // can see non-zero opening quantities even if not planned today.
      // We only include products with positive carry-over (routeInventory snapshot)
      const carryOverMap = routeInventory[activeRouteTab] || {};
      const planningCarryOverAllocations = Object.entries(carryOverMap)
        .filter(([pid, qty]) => (Number(qty) || 0) > 0)
        .map(([pid, qty]) => ({
          productId: pid,
          issuedQty: 0,           // still planning, nothing issued yet
          openingQty: Number(qty) || 0
        }));
  await setDoc(statusRef, {
        routeId: activeRouteTab,
  routeName: routes.find((r: any) => r.routeId === activeRouteTab)?.name || activeRouteTab,
        warehouseId: warehouses.find(w => w.id === selectedWarehouse)?.warehouseId || selectedWarehouse,
        warehouseName: warehouses.find(w => w.id === selectedWarehouse)?.name || '',
        date: selectedDate,
        status: 'planning',
        plannedLoads: updatedLoads,
        // Persist carry-over entries for visibility in planning state
        detailedAllocations: planningCarryOverAllocations,
        lastUpdatedBy: currentUser?.id || '',
        updatedAt: serverTimestamp(),
        ...(Object.keys(existingData).length === 0 && { createdAt: serverTimestamp(), createdBy: currentUser?.id || '' })
      }, { merge: true });
    } catch (err) {
      console.error('updateLoadQuantity (route) failed:', err);
    }
  };

  // Calculate total planned load for a route (sum of quantities)
  const calculateVehicleTotal = (routeId: string) => {
    const status = routeStates[routeId];
    if (status === 'issuing') {
      const data = routeIssuedAllocations[routeId] || {};
      return Object.values(data).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
    }
    const data = vehicleLoadData[routeId] || {};
    return Object.values(data).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
  };

  const handleAllocateProduct = (vehicleId: string, product: ProductStock, quantity: number) => {
    if (quantity <= 0 || quantity > product.availableStock) return;
    
    setVehicleLoads(prevLoads => 
      prevLoads.map(load => {
        if (load.vehicleId === vehicleId) {
          // Check if product already exists in allocations
          const existingAllocation = load.allocations.find(a => a.productId === product.productId);
          
          let newAllocations;
          if (existingAllocation) {
            // Update existing allocation
            newAllocations = load.allocations.map(a => 
              a.productId === product.productId 
                ? { ...a, allocatedQty: a.allocatedQty + quantity, totalValue: (a.allocatedQty + quantity) * a.rate }
                : a
            );
          } else {
            // Add new allocation
            const newAllocation: LoadoutAllocation = {
              productId: product.productId,
              productName: product.productName,
              unit: product.unit,
              availableStock: product.availableStock,
              allocatedQty: quantity,
              rate: 50, // Default rate - should come from pricing
              totalValue: quantity * 50
            };
            newAllocations = [...load.allocations, newAllocation];
          }
          
          // Calculate new current load (weight)
          const newCurrentLoad = newAllocations.reduce((sum, a) => sum + a.allocatedQty, 0);
          
          return {
            ...load,
            allocations: newAllocations,
            currentLoad: newCurrentLoad
          };
        }
        return load;
      })
    );
    
    // Update product stock availability
    setProductStocks(prevStocks => 
      prevStocks.map(stock => 
        stock.productId === product.productId 
          ? { ...stock, availableStock: stock.availableStock - quantity }
          : stock
      )
    );
  };

  const handleRemoveAllocation = (vehicleId: string, productId: string, quantity: number) => {
    setVehicleLoads(prevLoads => 
      prevLoads.map(load => {
        if (load.vehicleId === vehicleId) {
          const newAllocations = load.allocations.filter(a => a.productId !== productId);
          const newCurrentLoad = newAllocations.reduce((sum, a) => sum + a.allocatedQty, 0);
          
          return {
            ...load,
            allocations: newAllocations,
            currentLoad: newCurrentLoad
          };
        }
        return load;
      })
    );
    
    // Return stock to available inventory
    setProductStocks(prevStocks => 
      prevStocks.map(stock => 
        stock.productId === productId 
          ? { ...stock, availableStock: stock.availableStock + quantity }
          : stock
      )
    );
  };

  // Handle individual vehicle issue - SIMPLIFIED APPROACH
  const handleIssueToVehicle = async (vehicleId: string) => {
    try {
      const vehicle = vehicles.find(v => v.vehicleId === vehicleId);
      if (!vehicle) {
        alert('Vehicle not found!');
        return;
      }

      const plannedLoads = vehicleLoadData[vehicleId] || {};
      const hasLoads = Object.values(plannedLoads).some(qty => qty > 0);
      
      if (!hasLoads) {
        alert('No products planned for this vehicle!');
        return;
      }

      // Check if today's pricing is set
      const hasPricing = Object.keys(currentDayPricing).length > 0;
      if (!hasPricing) {
        alert('❌ Cannot proceed with loadout!\n\nToday\'s pricing has not been set. Please set pricing first.');
        return;
      }

      const timestamp = serverTimestamp();
      const beatId = `BEAT-${Date.now()}-${vehicleId.slice(-4)}`;

      let totalWeight = 0;
      let totalValue = 0;
      const allocations: LoadoutAllocation[] = [];

      // Build allocations array from planned loads
      for (const [productId, quantity] of Object.entries(plannedLoads)) {
        if (quantity > 0) {
          const product = productStocks.find(p => p.productId === productId);
          if (!product) continue;

          // Use current day's pricing (editable) or fallback to 0
          const rate = currentDayPricing[productId] || 0;
          
          // Warn if no pricing is set
          if (rate === 0) {
            const shouldContinue = window.confirm(`No price set for ${product.productName}. Continue with ₹0 rate?`);
            if (!shouldContinue) return;
          }
          
          const allocation: LoadoutAllocation = {
            productId,
            productName: product.productName,
            unit: product.unit,
            availableStock: product.availableStock,
            allocatedQty: quantity,
            rate,
            totalValue: quantity * rate
          };

          allocations.push(allocation);
          totalWeight += quantity;
          totalValue += allocation.totalValue;
        }
      }

      const warehouseDoc = warehouses.find(w => w.id === selectedWarehouse);
      const warehouseCustomId = warehouseDoc?.warehouseId || selectedWarehouse;

      // Atomic update: vehicle_daily_status + warehouse_daily_pricing
      await runTransaction(db, async (transaction) => {
        // 1. Update vehicle_daily_status
        const docId = `${vehicleId}_${selectedDate}`;
        const statusRef = doc(db, 'vehicle_daily_status', docId);
        transaction.set(statusRef, {
          vehicleId: vehicle.vehicleId,
          vehicleDocumentId: vehicleId,
          vehicleNumber: vehicle.vehicleNumber,
          warehouseId: selectedWarehouse,
          warehouseName: warehouseDoc?.name || '',
          date: selectedDate,
          status: 'issuing', // Status is issuing until salesman confirms
          plannedLoads: plannedLoads,
          
          // NEW: Enhanced fields for pending state (replaces beat_loadout WIP data)
          detailedAllocations: allocations,
          totalValue,
          totalWeight,
          beatId: beatId,
          
          // Audit fields
          lastUpdatedBy: currentUser?.id || '',
          issuedBy: currentUser?.id || '',
          issuedAt: timestamp,
          updatedAt: timestamp,
          createdAt: timestamp,
          createdBy: currentUser?.id || ''
        } as any, { merge: true });

        // 2. Update/Create warehouse_daily_pricing for each product with current pricing
        for (const allocation of allocations) {
          const pricingDocId = `${warehouseCustomId}_${selectedDate}_${allocation.productId}`;
          const pricingRef = doc(db, 'warehouse_daily_pricing', pricingDocId);
          
          transaction.set(pricingRef, {
            id: pricingDocId,
            warehouseId: warehouseCustomId,
            date: selectedDate,
            productId: allocation.productId,
            productName: allocation.productName,
            unitRate: allocation.rate,
            isActive: true,
            updatedAt: timestamp,
            updatedBy: currentUser?.id || 'system',
            createdAt: timestamp,
            createdBy: currentUser?.id || 'system'
          }, { merge: true });
        }
      });

      // Update local vehicle state to planned
      setVehicleStates(prev => ({
        ...prev,
        [vehicleId]: 'planned' as any // We'll need to add 'planned' to the type
      }));

      alert(`✅ Load-out planned for ${vehicle.vehicleNumber}!\n\nStatus: planned Confirmation\nItems planned: ${allocations.length}\nTotal weight: ${totalWeight} kg\nTotal value: ₹${totalValue.toLocaleString()}\n\nInventory will be transferred when salesman confirms the load.`);

      // 🔧 FIXED: Immediate state synchronization after issuing
      
      // Clear the planned loads since they're now saved as issued
      setVehicleLoadData(prev => ({
        ...prev,
        [vehicleId]: {}
      }));

      // Update vehicle status immediately in local state
      setVehicleStates(prev => ({
        ...prev,
        [vehicleId]: 'issuing'
      }));

      // NOTE: Immediate UI sync for legacy beat loadout removed (route_daily_status drives UI)

      // The onSnapshot listener will handle the long-term sync, but we've updated immediately for UI
      setTimeout(() => {
        loadRouteStatuses();
      }, 500);

      // Also reload warehouse data to show updated inventory
      await loadWarehouseData();

    } catch (error) {
      console.error('Error creating load-out plan:', error);
      alert('Error creating load-out plan. Please try again.');
    }
  };

  // NOTE: Deprecation retained as guardrail – Inventory creation occurs on salesman confirm
  const handleCompleteLoadout = async () => {
    alert('This function is deprecated. LoadoutPage only plans loadouts - inventory is created when salesman confirms.');
  };

  // Handle individual route issue - route-first approach
  const handleIssueToRoute = async (routeId: string) => {
    try {
      const plannedLoads = vehicleLoadData[routeId] || {};
      const hasLoads = Object.values(plannedLoads).some(qty => qty > 0);
      if (!hasLoads) {
        alert('No products planned for this route!');
        return;
      }
      // Validate that route inventory snapshot exists to compute accurate openingQty
      if (!routeInventory[routeId]) {
        console.warn('Route inventory snapshot missing; computing openingQty as 0');
      }

      const timestamp = serverTimestamp();
      let totalWeight = 0;
      let totalValue = 0;
      // Build issued allocations merged with carry-over set so all carry-forward items are present
      // 1) Start with all carry-over (openingQty > 0) with issuedQty = 0
      const carryOverMap = routeInventory[routeId] || {};
      const allocationMap: { [productId: string]: { productId: string; issuedQty: number; openingQty: number } } = {};
      Object.entries(carryOverMap).forEach(([pid, qty]) => {
        const opening = Number(qty) || 0;
        if (opening > 0) {
          allocationMap[pid] = { productId: pid, issuedQty: 0, openingQty: opening };
        }
      });
      // 2) Overlay today's planned issued quantities
      for (const [productId, quantity] of Object.entries(plannedLoads)) {
        const issued = Number(quantity) || 0;
        if (issued > 0) {
          const existing = allocationMap[productId];
          const openingQty = Number(routeInventory[routeId]?.[productId] || 0);
          allocationMap[productId] = {
            productId,
            issuedQty: issued,
            openingQty: existing ? existing.openingQty : openingQty
          };
          totalWeight += issued;
          const rate = currentDayPricing[productId] || 0;
          totalValue += issued * rate;
        }
      }
      const minimalAllocations = Object.values(allocationMap);

      const docId = `${routeId}_${selectedDate}`;
      const statusRef = doc(db, 'route_daily_status', docId);
      await setDoc(statusRef, {
        routeId,
  routeName: routes.find((r: any) => r.routeId === routeId)?.name || routeId,
        warehouseId: selectedWarehouseCustomId,
        warehouseName: warehouses.find(w => w.id === selectedWarehouse)?.name || '',
        date: selectedDate,
        status: 'issuing',
        plannedLoads,
        detailedAllocations: minimalAllocations,
        totalValue,
        totalWeight,
        lastUpdatedBy: currentUser?.id || '',
        issuedBy: currentUser?.id || '',
        issuedAt: timestamp,
        updatedAt: timestamp,
        createdAt: timestamp,
        createdBy: currentUser?.id || ''
      }, { merge: true });

      // Immediately reflect issued allocations locally to avoid UI jitter
      const immediateIssuedMap: { [productId: string]: number } = {};
      minimalAllocations.forEach((a: any) => {
        const qty = Number(a?.issuedQty ?? a?.allocatedQty ?? 0) || 0;
        if (a?.productId && qty > 0) immediateIssuedMap[a.productId] = qty;
      });
      setRouteIssuedAllocations(prev => ({
        ...prev,
        [routeId]: immediateIssuedMap
      }));

      // Clear the planned loads since they're now saved as issued
      setVehicleLoadData(prev => ({
        ...prev,
        [routeId]: {}
      }));

      setRouteStates(prev => ({
        ...prev,
        [routeId]: 'issuing'
      }));

      // Refresh statuses shortly; warehouse inventory will be reloaded by the route status listener
      setTimeout(() => {
        loadRouteStatuses();
      }, 300);

      alert(`✅ Load-out planned for ${routes.find((r: any) => (r.routeId || r.id) === routeId)?.name || routeId}!`);
    } catch (error) {
      console.error('Error creating load-out plan (route):', error);
      alert('Error creating load-out plan. Please try again.');
    }
  };

  // Calculate planned loadouts across all vehicles for a product
  // 🔧 FIXED: Only count planning/issuing vehicles - exclude confirmed vehicles (on_field+)
  const calculateplannedLoadouts = (productId: string) => {
    let total = 0;
    Object.keys(vehicleLoadData).forEach(routeId => {
      const status = routeStates[routeId];
      if (status === 'planning' || !status) {
        total += vehicleLoadData[routeId]?.[productId] || 0;
      } else if (status === 'issuing') {
        // When issuing, show the quantities from the issued allocations snapshot
        total += routeIssuedAllocations?.[routeId]?.[productId] || 0;
      }
    });
    return total;
  };

  // Calculate projected stock (available - planned) - shows actual negative values
  const calculateProjectedStock = (product: ProductStock) => {
    const plannedLoadouts = calculateplannedLoadouts(product.productId);
    return product.availableStock - plannedLoadouts;
  };

  // Calculate what projected stock would be with a hypothetical change
  const calculateWhatIfProjectedStock = (productId: string, newQuantity: number) => {
    const product = productStocks.find(p => p.productId === productId);
    if (!product) return 0;
    
    // Calculate planned loadouts excluding the current vehicle's current quantity
    let totalPlannedExcludingCurrent = 0;
    Object.keys(vehicleLoadData).forEach(routeId => {
      const status = routeStates[routeId];
      if (status === 'settled' || status === 'on_field' || status === 'settling') return;
      if (routeId === activeRouteTab) {
        totalPlannedExcludingCurrent += newQuantity;
      } else if (status === 'issuing') {
        totalPlannedExcludingCurrent += routeIssuedAllocations?.[routeId]?.[productId] || 0;
      } else {
        totalPlannedExcludingCurrent += vehicleLoadData[routeId]?.[productId] || 0;
      }
    });
    
    return product.availableStock - totalPlannedExcludingCurrent;
  };

  // Show over-allocation warning dialog
  const showOverAllocationWarning = (productId: string, newQuantity: number, projectedStock: number): Promise<boolean> => {
    return new Promise((resolve) => {
      const product = productStocks.find(p => p.productId === productId);
      if (!product) {
        resolve(false);
        return;
      }

  const currentPlanned = vehicleLoadData[activeRouteTab]?.[productId] || 0;
      const totalPlannedAll = calculateplannedLoadouts(productId) - currentPlanned + newQuantity;
      
      const message = `⚠️ Stock Over-Allocation Warning

Product: ${product.productName}
Available Stock: ${product.availableStock}
Current Planned (All Vehicles): ${calculateplannedLoadouts(productId)}
Your Change: ${currentPlanned} → ${newQuantity}
New Total Planned: ${totalPlannedAll}
Projected Stock: ${projectedStock}

This means you're planning to issue more than available stock.
Continue anyway?`;

      const shouldContinue = window.confirm(message);
      resolve(shouldContinue);
    });
  };

  // Check if product is over-allocated
  const isOverAllocated = (product: ProductStock) => {
    return calculateProjectedStock(product) < 0;
  };

  // Get current vehicle stock for a product - show actual inventory regardless of status
  const getVehicleCurrentStock = (routeId: string, productId: string) => {
    // For route-first, use routeInventory keyed by custom routeId
    return routeInventory[routeId]?.[productId] || 0;
  };

  // Calculate total value for a route using actual pricing
  const calculateRouteValue = (routeId: string) => {
    const status = routeStates[routeId];
    let total = 0;
    
    if (status === 'issuing') {
      const data = routeIssuedAllocations[routeId] || {};
      Object.entries(data).forEach(([productId, qty]) => {
        const price = currentDayPricing[productId] || 0;
        total += (Number(qty) || 0) * price;
      });
    } else {
      const data = vehicleLoadData[routeId] || {};
      Object.entries(data).forEach(([productId, qty]) => {
        const price = currentDayPricing[productId] || 0;
        total += (Number(qty) || 0) * price;
      });
    }
    
    return total;
  };

  // Get dummy previous day sales data
  const getDummyPrevDaySale = (productName: string) => {
    const dummyData: {[key: string]: number} = {
      'Apple Shimla': 45,
      'Apple - Kashmir': 32,
      'Apple Kinnaur': 28,
      'Banana - Robusta': 65,
      'Orange - Nagpur': 38,
      'Grapes - Green': 22
    };
    return dummyData[productName] || Math.floor(Math.random() * 50) + 10;
  };

  const selectedWarehouseName = warehouses.find(w => w.id === selectedWarehouse)?.name || '';
  const selectedWarehouseCustomId = warehouses.find(w => w.id === selectedWarehouse)?.warehouseId;
  const totalLoadedVehicles = Object.values(vehicleLoadData).filter(vehicleData => 
    Object.values(vehicleData).some(qty => qty > 0)
  ).length;
  const totalValue = Object.entries(vehicleLoadData).reduce((total, [routeKey, vehicleData]) => {
    return total + Object.entries(vehicleData).reduce((vehicleTotal, [productId, qty]) => {
      const price = currentDayPricing[productId] || 0;
      return vehicleTotal + (Number(qty) * price);
    }, 0);
  }, 0);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Section */}
      <div className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="py-4">
            {/* Title and Back Button */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-4">
                <button
                  onClick={onBack}
                  className="flex items-center space-x-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ArrowLeft className="w-5 h-5" />
                  <span>Back</span>
                </button>
                <div className="flex items-center space-x-3">
                  <Truck className="w-6 h-6 text-blue-600" />
                  <h1 className="text-xl font-semibold text-gray-800">Vehicle Load-out</h1>
                </div>
              </div>
              
              {/* Summary Stats */}
              <div className="flex items-center space-x-6 text-sm">
                <div className="text-center">
                  <div className="text-lg font-bold text-blue-600">
                    {totalLoadedVehicles}
                  </div>
                  <div className="text-gray-600">Loaded Vehicles</div>
                </div>
                <div className="text-center">
                  <div className="text-lg font-bold text-green-600">
                    ₹{totalValue.toLocaleString()}
                  </div>
                  <div className="text-gray-600">Total Value</div>
                </div>
              </div>
            </div>
            
            {/* Controls */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Warehouse Selector */}
                <div className="min-w-64">
                  <select
                    value={selectedWarehouse}
                    onChange={(e) => setSelectedWarehouse(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Warehouse</option>
                    {warehouses.map(warehouse => (
                      <option key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                {/* Date Display */}
                <div className="flex items-center space-x-2 text-sm text-gray-600">
                  <Clock className="w-4 h-4" />
                  <span>Date: {selectedDate}</span>
                </div>
              </div>
              
              {/* Action Buttons - Only show on localhost */}
              {isLocalhost && (
                <div className="flex items-center space-x-3">
                  {/* Sync to LIVE - requires confirmation */}
                  <button
                    onClick={() => setShowLiveSyncConfirm(true)}
                    disabled={syncLoading || !selectedWarehouse}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                    <span>{syncLoading ? 'Syncing...' : 'Sync to LIVE'}</span>
                  </button>

                  {/* Sync to TEST - direct */}
                  <button
                    onClick={() => handleSyncInventory(true)}
                    disabled={syncLoading || !selectedWarehouse}
                    className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`w-4 h-4 ${syncLoading ? 'animate-spin' : ''}`} />
                    <span>{syncLoading ? 'Syncing...' : 'Sync to TEST'}</span>
                  </button>

                  {/* Initialize System */}
                  <button
                    onClick={() => setShowInitializeModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <AlertTriangle className="w-4 h-4" />
                    <span>Initialize System</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!selectedWarehouse ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <Truck className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 mb-2">Select a Warehouse</h3>
            <p className="text-gray-600">Choose a warehouse to start loading vehicles</p>
          </div>
        </div>
      ) : loadoutBlocked ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-red-50 border-2 border-red-200 rounded-lg p-6">
            <div className="flex items-start space-x-3">
              <AlertTriangle className="w-8 h-8 text-red-600 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-red-800 mb-2">
                  ⚠️ Previous Routes Must Be Settled
                </h3>
                <p className="text-red-700 mb-4">
                  {blockingMessage}
                </p>
                <p className="text-red-700 text-sm mb-4">
                  Please complete settlement for previous routes before creating new loadouts.
                </p>
                <button
                  onClick={() => {/* TODO: Navigate to Settlement page */}}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Go to Settlement Page
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="space-y-6">
            
            {/* Enhanced Available Stock Section - 4 Row Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Available Stock</h2>
                <p className="text-sm text-gray-600">{selectedWarehouseName}</p>
              </div>
              
              <div className="p-4">
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse border border-gray-300">
                    {/* Product Names Header */}
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700 min-w-36">Stock Status</th>
                        {productStocks.map((product) => (
                          <th
                            key={product.productId}
                            className="border border-gray-300 px-4 py-2 text-center font-medium text-gray-700 min-w-24"
                          >
                            {product.productName}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    
                    <tbody>
                      {/* Row 1: Available Stock */}
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700 bg-gray-50">
                          Available Stock
                        </td>
                        {productStocks.map((product) => (
                          <td
                            key={product.productId}
                            className="border border-gray-300 px-4 py-2 text-center"
                          >
                            <span className={`font-semibold text-lg ${
                              product.availableStock === 0 ? 'text-red-600' :
                              product.availableStock <= 10 ? 'text-yellow-600' :
                              'text-green-600'
                            }`}>
                              {product.availableStock}
                            </span>
                          </td>
                        ))}
                      </tr>
                      
                      {/* Row 2: planned Loadouts */}
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700 bg-gray-50">
                          planned Loadouts
                        </td>
                        {productStocks.map((product) => (
                          <td
                            key={product.productId}
                            className="border border-gray-300 px-4 py-2 text-center"
                          >
                            <span className="font-semibold text-lg text-blue-600">
                              {calculateplannedLoadouts(product.productId)}
                            </span>
                          </td>
                        ))}
                      </tr>
                      
                      {/* Row 3: Projected Stock */}
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700 bg-gray-50">
                          Projected Stock
                        </td>
                        {productStocks.map((product) => {
                          const projectedStock = calculateProjectedStock(product);
                          const isOverAllocatedProduct = isOverAllocated(product);
                          
                          return (
                            <td
                              key={product.productId}
                              className="border border-gray-300 px-4 py-2 text-center"
                            >
                              <div className="flex items-center justify-center space-x-1">
                                {isOverAllocatedProduct && (
                                  <AlertCircle className="w-4 h-4 text-red-500" />
                                )}
                                <span className={`font-semibold text-lg ${
                                  isOverAllocatedProduct ? 'text-red-600' :
                                  projectedStock === 0 ? 'text-orange-600' :
                                  projectedStock <= 10 ? 'text-yellow-600' :
                                  'text-green-600'
                                }`}>
                                  {projectedStock}
                                </span>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      
                      {/* Row 4: Today's Price (Editable, defaults to Previous Day's Price) */}
                      <tr>
                        <td className="border border-gray-300 px-4 py-2 font-medium text-gray-700 bg-blue-50">
                          Today's Price
                          <div className="text-xs text-gray-500 font-normal">
                            (Yesterday: {Object.keys(prevDayPricing).length > 0 ? 'Available' : 'No data'})
                          </div>
                        </td>
                        {productStocks.map((product) => {
                          const currentPrice = currentDayPricing[product.productId] || 0;
                          const prevPrice = prevDayPricing[product.productId] || 0;
                          
                          return (
                            <td
                              key={product.productId}
                              className="border border-gray-300 px-2 py-2 text-center"
                            >
                              <div className="space-y-1">
                                <input
                                  type="number"
                                  value={currentPrice || ''}
                                  onChange={(e) => {
                                    const newPrice = parseFloat(e.target.value) || 0;
                                    handlePriceChange(product.productId, product.productName, newPrice);
                                  }}
                                  placeholder={prevPrice ? `${prevPrice}` : '0'}
                                  className="w-20 px-2 py-1 text-sm text-center border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  step="0.01"
                                  min="0"
                                />
                                {prevPrice > 0 && (
                                  <div className="text-xs text-gray-400">
                                    ↑ ₹{prevPrice}
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                      
                      {/* Row 5: Over-allocation Warning (if any) */}
                      {productStocks.some(product => isOverAllocated(product)) && (
                        <tr className="bg-red-50">
                          <td className="border border-gray-300 px-4 py-2 font-medium text-red-700 bg-red-100">
                            ⚠️ Over-allocated
                          </td>
                          {productStocks.map((product) => (
                            <td
                              key={product.productId}
                              className="border border-gray-300 px-4 py-2 text-center"
                            >
                              {isOverAllocated(product) && (
                                <span className="text-xs text-red-600 font-medium">
                                  -{Math.abs(calculateProjectedStock(product))}
                                </span>
                              )}
                            </td>
                          ))}
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            {/* Route Loading Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-800">Route Loading</h2>

                {/* Route Tabs */}
                <div className="mt-4 flex space-x-1 bg-gray-100 rounded-lg p-1">
                  {routes
                    .filter((r: any) => r.warehouseId === selectedWarehouseCustomId && !!r.routeId)
                    .map((r: any) => {
                      const key = r.routeId as string;
                      const isActive = activeRouteTab === key;
                      return (
                        <button
                          key={key}
                          onClick={() => setActiveRouteTab(key)}
                          className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                            isActive ? 'bg-white text-blue-600 shadow-sm' : 'text-gray-600 hover:text-gray-800'
                          }`}
                        >
                          <div className="text-center relative group">
                            <div className="font-medium" title={(routeAssignments[key]?.vehicleName || routeAssignments[key]?.vehicleId || 'No Vehicle') as string}>
                              {r.name || r.routeName || key}
                            </div>
                            <div className="mt-1 flex items-center justify-center gap-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700 border">
                                <User className="w-3 h-3 mr-1" />
                                {routeAssignments[key]?.salesmanName || 'No Salesman'}
                              </span>
                            </div>
                            {/* Hover tooltip for vehicle info */}
                            <div className="absolute left-1/2 -translate-x-1/2 top-full mt-2 whitespace-nowrap px-2 py-1 rounded bg-gray-800 text-white text-xs opacity-0 group-hover:opacity-100 pointer-events-none shadow-lg">
                              <div className="flex items-center">
                                <Truck className="w-3 h-3 mr-1" />
                                {routeAssignments[key]?.vehicleName || routeAssignments[key]?.vehicleId || 'No Vehicle'}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
                </div>
              
              {/* Route Loading Table */}
              {activeRouteTab && (
                  <div className="p-4">
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse border border-gray-300">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Category</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Product</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Prev Day Sale</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Current Stock</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Planned Load</th>
                            <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Total After Issue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {productStocks.map((product) => {
                            const currentRouteStock = routeInventory[activeRouteTab]?.[product.productId] || 0;
                            const routeStatus = routeStates[activeRouteTab];
                            // Show issued quantities during 'issuing', planned during 'planning', none after on_field
                            const plannedLoad =
                              (routeStatus === 'issuing')
                                ? (routeIssuedAllocations[activeRouteTab]?.[product.productId] || 0)
                                : (routeStatus === 'on_field' || routeStatus === 'settling' || routeStatus === 'settled')
                                  ? 0
                                  : (vehicleLoadData[activeRouteTab]?.[product.productId] || 0);
                            const totalAfterIssue = currentRouteStock + plannedLoad;
                            
                            return (
                              <tr key={product.productId} className="hover:bg-gray-50">
                                <td className="border border-gray-300 px-4 py-2 text-gray-600">{product.category}</td>
                                <td className="border border-gray-300 px-4 py-2 text-gray-800 font-medium">{product.productName}</td>
                                <td className="border border-gray-300 px-4 py-2 text-gray-600">{getDummyPrevDaySale(product.productName)}</td>
                                <td className="border border-gray-300 px-4 py-2 text-center">
                                  <span className="font-medium text-gray-700">
                                    {currentRouteStock}
                                  </span>
                                </td>
                                <td className="border border-gray-300 px-4 py-2">
                                  <input
                                    type="number"
                                    min="0"
                                    max={calculateProjectedStock(product) + plannedLoad}
                                    value={plannedLoad === 0 ? '' : plannedLoad}
                                    placeholder="0"
                                    onFocus={(e) => {
                                      e.target.select(); // Select all text on focus
                                    }}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      if (value === '') {
                                        updateLoadQuantity(product.productId, 0);
                                      } else {
                                        const numValue = parseInt(value) || 0;
                                        updateLoadQuantity(product.productId, numValue);
                                      }
                                    }}
                    disabled={routeStatus === 'issuing' || routeStatus === 'on_field' || routeStatus === 'settling' || routeStatus === 'settled' || loadoutBlocked}
                                    className={`w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                      (routeStatus === 'issuing' || routeStatus === 'on_field' || routeStatus === 'settling' || routeStatus === 'settled' || loadoutBlocked)
                                        ? 'bg-gray-100 cursor-not-allowed' 
                                        : ''
                                    }`}
                                  />
                                </td>
                                <td className="border border-gray-300 px-4 py-2 text-center">
                                  <span className="font-semibold text-blue-600">
                                    {totalAfterIssue}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                          {/* Total Row */}
                          <tr className="bg-gray-100 font-semibold">
                            <td className="border border-gray-300 px-4 py-2 text-gray-800" colSpan={4}>Total</td>
                            <td className="border border-gray-300 px-4 py-2 text-gray-800">
                              {calculateVehicleTotal(activeRouteTab)}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-gray-800">
                              {(() => {
                                const status = routeStates[activeRouteTab];
                                if (status === 'issuing') {
                                  return Object.values(routeIssuedAllocations[activeRouteTab] || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
                                }
                                return Object.values(vehicleLoadData[activeRouteTab] || {}).reduce((sum, qty) => sum + (Number(qty) || 0), 0);
                              })()}
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Route Summary */}
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div>
                          <h4 className="font-medium text-gray-800">{routes.find((r: any) => r.routeId === activeRouteTab)?.name || 'Route'} Summary</h4>
                          <p className="text-sm text-gray-600">
                            Total Load: {calculateVehicleTotal(activeRouteTab)} kg
                          </p>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className="text-lg font-bold text-green-600">
                              ₹{calculateRouteValue(activeRouteTab).toLocaleString()}
                            </div>
                            <div className="text-sm text-gray-600">Estimated Value</div>
                          </div>
                          
                          {/* Individual Issue Button */}
                          <button
                            onClick={() => handleIssueToRoute(activeRouteTab)}
                            disabled={
                              loadoutBlocked ||
                              calculateVehicleTotal(activeRouteTab) === 0 || 
                              routeStates[activeRouteTab] !== 'planning'
                            }
                            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <Send className="w-4 h-4" />
                            <span>
                              {routeStates[activeRouteTab] === 'issuing' ? 'Issued' : 
                               routeStates[activeRouteTab] === 'settled' ? 'Completed' : 'Issue'}
                            </span>
                          </button>
                        </div>
                      </div>
                      {/* No capacity bar for routes at this time */}
                    </div>
                  </div>
                )}
            </div>
          </div>
        </div>
      )}

      {/* System Initialization Modal */}
      {showInitializeModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Initialize System</h3>
                <button
                  onClick={() => {
                    setShowInitializeModal(false);
                    // cleanup-only flow
                    setInitializationProgress(null);
                    setInitializationResult('');
                    setConfirmText('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!initializationProgress && !initializationResult && (
                <div className="space-y-6">
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-red-800">⚠️ System Reset Warning</h4>
                        <p className="text-sm text-red-700 mt-1">
                          This will permanently delete ALL transactional data and reset the system to a clean development state. No CSV is required.
                        </p>
                        <div className="mt-2 text-sm text-red-600">
                          <p><strong>Collections to be cleared:</strong></p>
                          <ul className="list-disc list-inside ml-2 space-y-1">
                            <li>inventory_journal (audit trails)</li>
                            <li>beat_loadout (loadout assignments)</li>
                            <li>outletVisits (visit records)</li>
                            <li>salesDetails (sale line items)</li>
                            <li>salesHeaders (sale transactions)</li>
                            <li>route_daily_status (route states)</li>
                            <li>inventory (all records)</li>
                            <li>vehicle_daily_status (vehicle day states)</li>
                            <li>settlements</li>
                            <li>retailer_sales</li>
                            <li>zoho_sync_status</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">Type RESET to confirm</label>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="RESET"
                        className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                      <p className="text-xs text-gray-500 mt-1">This is a destructive action. Beats will be preserved.</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <h4 className="font-medium text-blue-800 mb-2">What this will do:</h4>
                          <ul className="text-sm text-blue-700 space-y-1">
                            <li>1. Delete all transactional records (preserving master data)</li>
                            <li>2. Clear inventory and journals, then seed 4 baseline inventory records (PRD001/PRD002 x MUM-WH01/MUM-WH02)</li>
                            <li>3. Clear route_daily_status (beats are preserved)</li>
                            <li>4. Reset system to clean development state</li>
                          </ul>
                    </div>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setShowInitializeModal(false);
                        setConfirmText('');
                      }}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async () => {
                        try {
                          setInitializationProgress({ stage: 'starting', message: 'Starting system initialization...', progress: 0 });
                          
                          const result = await SystemInitializationService.initializeSystem((progress) => setInitializationProgress(progress));
                          
                          setInitializationResult(result.message);
                          setInitializationProgress(null);
                          
                          // Refresh the page data after successful initialization
                          if (result.success) {
                            setTimeout(() => {
                              loadInitialData();
                            }, 2000);
                          }
                          
                        } catch (error) {
                          setInitializationResult(`❌ Initialization Failed: ${error}`);
                          setInitializationProgress(null);
                        }
                      }}
                      disabled={confirmText.trim().toUpperCase() !== 'RESET'}
                      
                      className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Initialize System
                    </button>
                  </div>
                </div>
              )}

              {/* Progress Display */}
              {initializationProgress && (
                <div className="space-y-6">
                  <div className="text-center">
                    <Loader className="w-12 h-12 text-blue-500 mx-auto mb-4 animate-spin" />
                    <h4 className="text-lg font-medium text-gray-800">System Initialization in Progress</h4>
                    <p className="text-gray-600 mt-2">{initializationProgress.message}</p>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="capitalize">{initializationProgress.stage}</span>
                      <span>{initializationProgress.progress}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${initializationProgress.progress}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="w-5 h-5 text-yellow-500 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800">Please wait...</p>
                        <p className="text-sm text-yellow-700">
                          Do not close this window or refresh the page during initialization.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Results Display */}
              {initializationResult && (
                <div className="space-y-6">
                  <div className="text-center">
                    {initializationResult.includes('✅') ? (
                      <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                    ) : (
                      <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                    )}
                  </div>

                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                      {initializationResult}
                    </pre>
                  </div>

                  <div className="flex space-x-4">
                    <button
                      onClick={() => {
                        setShowInitializeModal(false);
                        setInitializationResult('');
                      }}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      Close
                    </button>
                    {initializationResult.includes('✅') && (
                      <button
                        onClick={() => window.location.reload()}
                        className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                      >
                        Refresh Page
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* LIVE Sync Confirmation Modal */}
      {showLiveSyncConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center space-x-3 mb-4">
                <div className="flex-shrink-0">
                  <AlertTriangle className="w-8 h-8 text-red-600" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Confirm LIVE Sync</h3>
                  <p className="text-sm text-gray-600 mt-1">This will sync to production database</p>
                </div>
              </div>
              
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">
                  <strong>⚠️ Warning:</strong> You are about to sync data to the <strong>LIVE Firebase</strong> database (sfa-system-8a445).
                </p>
                <p className="text-sm text-red-700 mt-2">
                  This will update production settlement records and sync with Tally ERP.
                </p>
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => setShowLiveSyncConfirm(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowLiveSyncConfirm(false);
                    handleSyncInventory(false);
                  }}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Proceed with LIVE Sync
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Pricing Modal */}
      {showPricingModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-semibold text-gray-800">Set Today's Pricing</h3>
                <button
                  onClick={() => setShowPricingModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="space-y-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-blue-800">Pricing Initialization Required</h4>
                      <p className="text-sm text-blue-700 mt-1">
                        Today's pricing has not been set yet. Please review and set prices for all products before proceeding with loadouts.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Table */}
                <div className="overflow-x-auto overflow-y-auto max-h-96">
                  <table className="w-full border-collapse border border-gray-300">
                    <thead>
                      <tr className="bg-gray-50">
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Product</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Category</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Unit</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Last Available Price</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Date</th>
                        <th className="border border-gray-300 px-4 py-2 text-left font-medium text-gray-700">Today's Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {allProducts.map((product) => {
                        const lastPricing = lastAvailablePricing[product.productId];
                        const currentModalPrice = modalPricing[product.productId] || 0;
                        
                        return (
                          <tr key={product.productId} className="hover:bg-gray-50">
                            <td className="border border-gray-300 px-4 py-2 font-medium text-gray-800">
                              {product.productName}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-gray-600">
                              {product.category}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-gray-600">
                              {product.unit}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center">
                              {lastPricing ? (
                                <span className="font-medium text-gray-700">
                                  ₹{lastPricing.rate.toFixed(2)}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">No history</span>
                              )}
                            </td>
                            <td className="border border-gray-300 px-4 py-2 text-center text-sm text-gray-500">
                              {lastPricing ? lastPricing.date : '-'}
                            </td>
                            <td className="border border-gray-300 px-4 py-2">
                              <input
                                type="number"
                                value={currentModalPrice || ''}
                                onChange={(e) => {
                                  const newPrice = parseFloat(e.target.value) || 0;
                                  setModalPricing(prev => ({
                                    ...prev,
                                    [product.productId]: newPrice
                                  }));
                                }}
                                placeholder={lastPricing ? lastPricing.rate.toString() : '0'}
                                className="w-24 px-3 py-2 text-center border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                step="0.01"
                                min="0"
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-2">Summary</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-600">Total Products:</span>
                      <span className="ml-2 font-medium">{productStocks.length}</span>
                    </div>
                    <div>
                      <span className="text-gray-600">Products with Last Price:</span>
                      <span className="ml-2 font-medium">
                        {Object.values(lastAvailablePricing).filter(p => p.rate > 0).length}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-600">Products with Today's Price Set:</span>
                      <span className="ml-2 font-medium">
                        {Object.values(modalPricing).filter(price => price > 0).length}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex space-x-4">
                  <button
                    onClick={() => setShowPricingModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      // Copy last available prices to modal pricing for missing entries
                      const updatedModalPricing = { ...modalPricing };
                      productStocks.forEach(product => {
                        if (!updatedModalPricing[product.productId] || updatedModalPricing[product.productId] === 0) {
                          const lastPrice = lastAvailablePricing[product.productId]?.rate || 0;
                          if (lastPrice > 0) {
                            updatedModalPricing[product.productId] = lastPrice;
                          }
                        }
                      });
                      setModalPricing(updatedModalPricing);
                    }}
                    className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  >
                    Copy Last Prices
                  </button>
                  <button
                    onClick={handleSetTodaysPricing}
                    disabled={Object.values(modalPricing).every(price => price === 0)}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Set Today's Pricing
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Zoho modal removed – Tally sync uses simple success/error alerts */}
    </div>
  );
};

export default LoadoutPage;
